import { isCloudTenantTarget } from '@/pages/library/components/useCloudTenantTarget'
import { getAuthConfig } from '@/services/auth/config'

import { getAssistantSurface, type LibrarySurfaceHandle } from './assistant-actions'
import type { ExecuteClientToolDeps } from './client-tool-types'

const LIBRARY_PATH = '/library'

/** The Library list loads its lists before it registers; past this the dialog is reported as not opened. */
const OPEN_WAIT_MS = 4_000
const POLL_MS = 100

const SOURCES = ['file', 'server'] as const
type ImportSource = (typeof SOURCES)[number]

const delay = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms))

function readSource(input: unknown): ImportSource | null {
  const source = (input as { source?: unknown } | null)?.source

  return SOURCES.includes(source as ImportSource) ? (source as ImportSource) : null
}

/** `isCloudTenantTarget` for this caller. A config that has not loaded yet reads as cloud, as `useCloudTenantTarget` does. */
function isCloudTenant(tenantId: string | null | undefined): boolean {
  let provider: 'cognito' | 'keycloak' | undefined

  try {
    provider = getAuthConfig().provider
  } catch {
    provider = 'cognito'
  }

  return isCloudTenantTarget({ provider, tenantId })
}

/** The Library LIST page's `openImport`; `ItemDetail` registers the same kind without it. */
function listOpenImport(): NonNullable<LibrarySurfaceHandle['openImport']> | null {
  const handle = getAssistantSurface()

  return handle?.kind === 'library' && handle.openImport ? handle.openImport : null
}

/** Polls until the list page registers its `openImport`, checking once more after the last wait. */
async function awaitListOpenImport() {
  const deadline = Date.now() + OPEN_WAIT_MS

  for (;;) {
    const openImport = listOpenImport()
    if (openImport) return openImport
    if (Date.now() >= deadline) return null
    await delay(POLL_MS)
  }
}

/**
 * `open_import_dialog`. `refusal` is the route manifest's answer for `/library` for this caller,
 * resolved by `executeClientTool` (which owns the resolver), or null when allowed.
 */
export async function executeOpenImportDialog(
  input: unknown,
  deps: ExecuteClientToolDeps,
  refusal: string | null,
): Promise<unknown> {
  const source = readSource(input)
  if (!source) return { error: "Invalid source: expected 'file' or 'server'." }

  if (refusal) return { refused: refusal }

  const role = deps.caller?.role ?? 'viewer'

  if (role !== 'admin' && role !== 'publisher') {
    return { refused: 'Only publishers and admins can import into the Library.' }
  }

  if (source === 'server' && isCloudTenant(deps.caller?.tenantId)) {
    return { refused: 'There are no connected servers on Gisify cloud; use the file source instead.' }
  }

  if (deps.currentRoute() !== LIBRARY_PATH) {
    const discard = (input as { discardUnsaved?: unknown }).discardUnsaved === true

    if (!discard) {
      const unsaved = deps.hasUnsavedWork()
      if (unsaved) return { blocked_unsaved_work: unsaved }
    }

    deps.navigate(LIBRARY_PATH)
  }

  const openImport = await awaitListOpenImport()
  if (!openImport) return { error: 'The Library did not open in time.' }

  openImport(source)

  return { opened: source }
}
